<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType, INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';
import WhatsAppCampaignMetrics from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignMetrics.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const STATUS_COMPLETED = 'completed';
const DISPATCHED_STATUSES = ['processing', 'completed', 'paused'];

const showMetrics = ref(false);

// Mostra métricas só p/ campanha WhatsApp, com flag ativa e que já disparou
// (status avançado OU já tem envios contabilizados).
const isWhatsAppCampaign = computed(
  () => props.inbox?.channel_type === INBOX_TYPES.WHATSAPP
);

const hasDispatched = computed(() => {
  const c = props.campaign;
  if (!c) return false;
  return DISPATCHED_STATUSES.includes(c.campaign_status) || (c.sent_count ?? 0) > 0;
});

const canShowMetrics = computed(
  () =>
    isWhatsAppCampaign.value &&
    hasDispatched.value &&
    isCloudFeatureEnabled(FEATURE_FLAGS.WHATSAPP_CAMPAIGNS)
);

const toggleMetrics = () => {
  showMetrics.value = !showMetrics.value;
};

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<template>
  <CardLayout layout="col">
    <div
      class="flex flex-row items-center justify-between gap-4 w-full"
    >
      <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
      <div class="flex items-center justify-end gap-2">
        <Button
          v-if="canShowMetrics"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-bar-chart-3"
          :label="t('CAMPAIGN.WHATSAPP.METRICS.TITLE')"
          @click="toggleMetrics"
        />
        <Button
          v-if="isLiveChatType"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-sliders-vertical"
          @click="emit('edit')"
        />
        <Button
          variant="faded"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          @click="emit('delete')"
        />
      </div>
    </div>

    <div
      v-if="canShowMetrics && showMetrics"
      class="w-full pt-4 mt-2 border-t border-n-weak"
    >
      <WhatsAppCampaignMetrics :campaign="campaign" />
    </div>
  </CardLayout>
</template>
